import { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';

// BarcodeDetector (Chrome / Android WebView) is not in the TypeScript DOM library yet.
interface DetectedBarcode {
  readonly rawValue: string;
}
interface BarcodeDetectorInstance {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorConstructor = new () => BarcodeDetectorInstance;

const detectorClass = () => (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;

/** Needs the browser's barcode detector and a secure context (https or localhost) for the camera. */
export const cameraSupported = () => Boolean(detectorClass() && window.isSecureContext && navigator.mediaDevices?.getUserMedia);

/** Full-screen camera that resolves with the first barcode it reads. */
export function CameraScanner({ onResult, onClose }: { onResult: (code: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const result = useRef(onResult);
  result.current = onResult;
  const [status, setStatus] = useState('Kamera açılıyor…');
  const [track, setTrack] = useState<MediaStreamTrack | null>(null);
  const [torch, setTorch] = useState(false);

  useEffect(() => {
    const Detector = detectorClass();
    if (!Detector) return;
    const detector = new Detector();
    let stream: MediaStream | null = null;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (stopped || !video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        setTrack(stream.getVideoTracks()[0] ?? null);
        setStatus('Barkodu çerçevenin içine getirin');
        const tick = async () => {
          if (stopped || !video.current) return;
          try {
            const [found] = await detector.detect(video.current);
            if (found?.rawValue) {
              result.current(found.rawValue);
              return;
            }
          } catch {
            // a frame that could not be read: try the next one
          }
          timer = setTimeout(tick, 120);
        };
        void tick();
      } catch {
        setStatus('Kameraya erişilemedi. Tarayıcıda kamera iznini verin.');
      }
    })();
    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const torchSupported = Boolean(track && 'torch' in (track.getCapabilities?.() ?? {}));

  return (
    <div className="camera" role="dialog" aria-label="Kamera ile barkod okuma">
      <video ref={video} playsInline muted />
      <div className="frame" />
      <div className="bar">
        <span>{status}</span>
        <span className="row">
          {torchSupported && (
            <button
              className="btn"
              onClick={async () => {
                await track!.applyConstraints({ advanced: [{ torch: !torch } as MediaTrackConstraintSet] });
                setTorch(!torch);
              }}
            >
              Fener
            </button>
          )}
          <button className="btn" onClick={onClose}>
            <Icon name="x" /> Kapat
          </button>
        </span>
      </div>
    </div>
  );
}
